import camelcaseKeys from "camelcase-keys";
import * as v from "valibot";

import type { EqLocationTypeId } from "../../enums";

const epicenterScheme = v.pipe(
    v.looseObject({}),
    v.transform((x) => camelcaseKeys(x, { deep: true })),
    v.object({
        /** 震源ID */
        eqSourceId: v.string(),

        /** 気象庁月報オリジンタイム */
        jemOriginTime: v.string(),

        /** 気象庁月報震源緯度 */
        jemLat: v.pipe(
            v.number(),
            v.minValue(-90),
            v.maxValue(90)),

        jemLatStr: v.string(),

        /** 気象庁月報震源経度 */
        jemLon: v.pipe(
            v.number(),
            v.minValue(-180),
            v.maxValue(180)),

        jemLonStr: v.string(),

        /** 気象庁月報震源深さ */
        jemDepth: v.number(),

        jemDepthStr: v.string(),

        /** 気象庁月報マグニチュード */
        mjma: v.number(),

        mjmaStr: v.string(),

        /** 地震発生位置種別ID */
        eqLocationTypeId: v.pipe(
            v.number(),
            v.integer(),
            v.minValue(0)),

        /** F-netオリジンタイム */
        nfOriginTime: v.nullable(v.string()),

        /** F-net震源緯度 */
        nfLat: v.nullable(
            v.pipe(
                v.number(),
                v.minValue(-90),
                v.maxValue(90))),

        nfLatStr: v.nullable(v.string()),

        /** F-net震源経度 */
        nfLon: v.nullable(
            v.pipe(
                v.number(),
                v.minValue(-180),
                v.maxValue(180))),

        nfLonStr: v.nullable(v.string()),

        /** F-net震源深さ */
        nfDepth: v.nullable(v.number()),

        nfDepthStr: v.nullable(v.string()),

        /** F-netモーメントマグニチュード */
        mw: v.nullable(v.number()),

        mwStr: v.nullable(v.string()),

        /** F-net走向角1 */
        strike1: v.nullable(v.number()),

        strike1Str: v.nullable(v.string()),

        /** F-net傾斜角1 */
        dip1: v.nullable(v.number()),

        dip1Str: v.nullable(v.string()),

        /** F-netすべり角1 */
        rake1: v.nullable(v.number()),

        rake1Str: v.nullable(v.string()),

        /** F-net走向角2 */
        strike2: v.nullable(v.number()),

        strike2Str: v.nullable(v.string()),

        /** F-net傾斜角2 */
        dip2: v.nullable(v.number()),

        dip2Str: v.nullable(v.string()),

        /** F-netすべり角2 */
        rake2: v.nullable(v.number()),

        rake2Str: v.nullable(v.string()),

        /** F-netメカニズム種別ID */
        eqMechanismTypeId: v.nullable(
            v.pipe(
                v.number(),
                v.integer(),
                v.minValue(0))),

        /** F-netセントロイド深さ */
        cmtDepth: v.nullable(v.number()),

        cmtDepthStr: v.nullable(v.string()),

        /** F-netバリアンスリダクション */
        varred: v.nullable(v.number()),

        varredStr: v.nullable(v.string()),

        /** F-netMxx */
        mxx: v.nullable(v.number()),

        mxxStr: v.nullable(v.string()),

        /** F-netMxy */
        mxy: v.nullable(v.number()),

        mxyStr: v.nullable(v.string()),

        /** F-netMxz */
        mxz: v.nullable(v.number()),

        mxzStr: v.nullable(v.string()),

        /** F-netMyy */
        myy: v.nullable(v.number()),

        myyStr: v.nullable(v.string()),

        /** F-netMyz */
        myz: v.nullable(v.number()),

        myzStr: v.nullable(v.string()),

        /** F-netMzz */
        mzz: v.nullable(v.number()),

        mzzStr: v.nullable(v.string()),

        /** F-netMpp単位 */
        exp: v.nullable(v.number()),

        expStr: v.nullable(v.string()),

        /** 断層セグメント情報オブジェクトの配列 */
        faultSegments: v.array(
            v.object({
                /** セグメントID */
                segmentIdx: v.pipe(
                    v.number(),
                    v.integer(),
                    v.minValue(0)),

                /** 断層セグメント幅 */
                width: v.nullable(v.number()),

                widthStr: v.nullable(v.string()),

                /** 断層セグメント長さ */
                length: v.nullable(v.number()),

                lengthStr: v.nullable(v.string()),

                /** 断層セグメント上端中心緯度 */
                topCenterLat: v.nullable(
                    v.pipe(
                        v.number(),
                        v.minValue(-90),
                        v.maxValue(90))),

                topCenterLatStr: v.nullable(v.string()),

                /** 断層セグメント上端中心経度 */
                topCenterLon: v.nullable(
                    v.pipe(
                        v.number(),
                        v.minValue(-180),
                        v.maxValue(180))),

                topCenterLonStr: v.nullable(v.string()),

                /** 断層セグメント走向角 */
                strikeDeg: v.nullable(v.number()),

                strikeDegStr: v.nullable(v.string()),

                /** 断層セグメント傾斜角 */
                dipDeg: v.nullable(v.number()),

                dipDegStr: v.nullable(v.string()),

                /** 断層セグメント上端深さ */
                hTop: v.nullable(v.number()),

                hTopStr: v.nullable(v.string()),
            })),

        /** 地震発生位置種別の評価方法 */
        eqLocationTypeIdSource: v.nullable(
            v.pipe(
                v.number(),
                v.integer(),
                v.minValue(0))),

        /** 波形データ数 */
        dataCount: v.number(),
    }),
    v.transform((x) => {
        return {
            ...x,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            eqLocationTypeId: x.eqLocationTypeId as EqLocationTypeId,
            hasFnetData: [
                x.nfOriginTime, x.nfLat, x.nfLon, x.nfDepth,
                x.mw, x.strike1, x.dip1, x.rake1,
                x.strike2, x.dip2, x.rake2, x.cmtDepth,
                x.varred, x.mxx, x.mxy, x.mxz,
                x.myy, x.myz, x.mzz, x.exp,
            ].some((y) => y != null),
        };
    }));

export type Epicenter = v.InferOutput<typeof epicenterScheme>;

/**
 * 与えられたデータを震源情報データとしてパースする。
 *
 * @param data 対象のデータ
 * @returns パース結果
 */
export const parseEpicenter = (data: unknown) => {
    return v.parse(epicenterScheme, data);
};

/**
 * 与えられたデータを震源情報データの配列としてパースする。
 *
 * @param data 対象のデータ
 * @returns パース結果
 */
export const parseEpicenters = (data: unknown) => {
    if (Array.isArray(data)) {
        return data.map((x) => parseEpicenter(x));
    } else {
        throw new TypeError("data is not an array.");
    }
};
